import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Badge } from '../ui'
import {
  DemoHeader,
  InsightCard,
  OntologyResponsePanel,
  UserActionPanel,
} from './GoalDrivenDemo'
import { useT } from '../../lib/i18n'
import type { LabDemoProps } from './types'
import type { ContinuousAuditLocale } from '../../lib/locales'

type AuditState = 'idle' | 'running' | 'done'

const SEVERITY_TONE: Record<'critical' | 'warn' | 'info', 'block' | 'warn' | 'info'> = {
  critical: 'block',
  warn: 'warn',
  info: 'info',
}

const SCAN_INTERVAL_MS = 220
const FINDING_INTERVAL_MS = 260
const SCAN_TOTAL = 24

export function ContinuousAuditDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.continuousAudit | undefined) ?? t.labDemos.continuousAudit
  const [state, setState] = useState<AuditState>('idle')
  const [scanned, setScanned] = useState(0)
  const [visibleCount, setVisibleCount] = useState(0)
  const [expandedFindingId, setExpandedFindingId] = useState<string | null>(null)
  const scanRef = useRef<number | null>(null)
  const findRef = useRef<number | null>(null)

  const summary = useMemo(() => {
    const visible = demo.findings.slice(0, visibleCount)
    const critical = visible.filter((f) => f.severity === 'critical').length
    const warn = visible.filter((f) => f.severity === 'warn').length
    const info = visible.filter((f) => f.severity === 'info').length
    return { critical, warn, info, total: visible.length }
  }, [demo.findings, visibleCount])

  useEffect(() => {
    return () => {
      if (scanRef.current) window.clearInterval(scanRef.current)
      if (findRef.current) window.clearInterval(findRef.current)
    }
  }, [])

  const startAudit = () => {
    setState('running')
    setScanned(0)
    setVisibleCount(0)
    setExpandedFindingId(null)

    if (scanRef.current) window.clearInterval(scanRef.current)
    if (findRef.current) window.clearInterval(findRef.current)

    scanRef.current = window.setInterval(() => {
      setScanned((s) => {
        if (s >= SCAN_TOTAL) {
          if (scanRef.current) window.clearInterval(scanRef.current)
          return SCAN_TOTAL
        }
        return s + 1
      })
    }, SCAN_INTERVAL_MS)

    findRef.current = window.setInterval(() => {
      setVisibleCount((c) => {
        if (c >= demo.findings.length) {
          if (findRef.current) window.clearInterval(findRef.current)
          setState('done')
          return demo.findings.length
        }
        return c + 1
      })
    }, FINDING_INTERVAL_MS)
  }

  const reset = () => {
    if (scanRef.current) window.clearInterval(scanRef.current)
    if (findRef.current) window.clearInterval(findRef.current)
    setState('idle')
    setScanned(0)
    setVisibleCount(0)
    setExpandedFindingId(null)
  }

  const progressPct = (scanned / SCAN_TOTAL) * 100
  const progressText = demo.progressTpl
    .replace('{scanned}', String(scanned))
    .replace('{total}', String(SCAN_TOTAL))

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={demo.controlLabel}
          hint={demo.controlHint}
        >
          <div className="flex items-center gap-3 flex-wrap">
            {state === 'idle' && (
              <button
                type="button"
                onClick={startAudit}
                className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-4 py-2 rounded-[var(--radius-sm)] bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium hover:bg-[color:var(--color-orion-amber-dim)] transition-colors"
              >
                {demo.runButton}
              </button>
            )}
            {state !== 'idle' && (
              <button
                type="button"
                onClick={reset}
                className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-3 py-2 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-night)] transition-colors"
              >
                {demo.resetButton}
              </button>
            )}
            <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-haze)]">
              {state === 'running' ? demo.runningLabel : progressText}
            </span>
          </div>
          {state !== 'idle' && (
            <div className="mt-3 h-1.5 rounded-full bg-[color:var(--color-orion-surface)]/40 overflow-hidden">
              <div
                className="h-full bg-[color:var(--color-orion-amber)] transition-[width] duration-200"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          )}
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          {state === 'idle' ? (
            <EmptyState title={demo.emptyStateTitle} body={demo.emptyStateBody} />
          ) : (
            <div className="space-y-3">
              <SummaryBar
                count={summary.total}
                critical={summary.critical}
                warn={summary.warn}
                info={summary.info}
                demo={demo}
              />
              <div className="grid sm:grid-cols-2 gap-2">
                <AnimatePresence initial={false}>
                  {demo.findings.slice(0, visibleCount).map((f, i) => (
                    <motion.div
                      key={f.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(0.05 * i, 0.4) }}
                    >
                      <FindingCard
                        finding={f}
                        labels={demo}
                        expanded={expandedFindingId === f.id}
                        onToggle={() =>
                          setExpandedFindingId((curr) => (curr === f.id ? null : f.id))
                        }
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

function SummaryBar({
  count,
  critical,
  warn,
  info,
  demo,
}: {
  count: number
  critical: number
  warn: number
  info: number
  demo: ContinuousAuditLocale
}) {
  const countText = demo.summaryCountTpl.replace('{count}', String(count))
  const breakdown = demo.bySeverityTpl
    .replace('{critical}', String(critical))
    .replace('{warn}', String(warn))
    .replace('{info}', String(info))
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
        {demo.summaryTitle}
      </span>
      <Badge tone={critical > 0 ? 'block' : warn > 0 ? 'warn' : 'pass'}>{countText}</Badge>
      <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
        {breakdown}
      </span>
    </div>
  )
}

function FindingCard({
  finding,
  labels,
  expanded,
  onToggle,
}: {
  finding: ContinuousAuditLocale['findings'][number]
  labels: ContinuousAuditLocale
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <div
      className={`rounded-[var(--radius-md)] p-3.5 border ${
        expanded
          ? 'border-[color:var(--color-orion-amber)]/55 bg-[color:var(--color-orion-amber)]/8'
          : 'border-[color:var(--color-orion-surface)]/40 bg-[color:var(--color-orion-deep)]/30'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1.5 flex-wrap">
        <div className="flex items-baseline gap-2 flex-wrap">
          <Badge tone={SEVERITY_TONE[finding.severity]}>{labels.severityLabels[finding.severity]}</Badge>
          <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-text)]">
            {finding.asset}
          </span>
          <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
            {finding.rule}
          </span>
        </div>
      </div>
      <div
        className="text-[color:var(--color-orion-text)] mb-1"
        style={{ fontSize: 'var(--text-small)' }}
      >
        {finding.title}
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] leading-snug mb-2"
        style={{ fontSize: 'var(--text-micro)' }}
      >
        {finding.detail}
      </p>
      <button
        type="button"
        onClick={onToggle}
        className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] hover:text-[color:var(--color-orion-amber-dim)] transition-colors"
      >
        {labels.draftFixButton}
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 pt-2 border-t border-[color:var(--color-orion-amber)]/30">
              <p
                className="text-[color:var(--color-orion-text)] leading-snug"
                style={{ fontSize: 'var(--text-small)' }}
              >
                {finding.fixDraft}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-[200px] flex flex-col items-center justify-center text-center px-6">
      <div className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
        {title}
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] max-w-md"
        style={{ fontSize: 'var(--text-small)', lineHeight: 1.6 }}
      >
        {body}
      </p>
    </div>
  )
}
